import { useEffect, useRef, useState } from "react";
import { HiOutlineSparkles } from "react-icons/hi2";

interface LouiseBotWidgetProps {
  botId: string;
  mode?: "floating" | "inline";
  containerId?: string;
  onLoad?: () => void;
  mute?: boolean;
  open?: boolean;
  hideMinimizeButton?: boolean;
  hideCloseButton?: boolean;
  loadingText?: string;
}

const LouiseBotWidget = ({
  botId,
  mode = "floating",
  containerId = "louisebot-inline-container",
  onLoad,
  mute = false,
  open = false,
  hideMinimizeButton = false,
  hideCloseButton = false,
  loadingText = "Loading bot...",
}: LouiseBotWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const fallbackTimeout = 100;

  useEffect(() => {
    if (!botId) return;

    if (mode === "floating") {
      localStorage.setItem("ps_widget", botId);

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = process.env.REACT_APP_LOUISE_BOT_PATH as string;

      script.id = "louisebot-widget-script";

      document.head.appendChild(script);

      return () => {
        const scriptElement = document.getElementById(
          "louisebot-widget-script"
        );
        if (scriptElement) {
          scriptElement.remove();
        }

        localStorage.removeItem("ps_widget");

        const widgetElements = document.querySelectorAll(
          '[id^="louisebot"], [class*="louisebot"]'
        );
        widgetElements.forEach((element) => element.remove());
      };
    } else if (mode === "inline") {
      const timeoutId = setTimeout(() => {
        if (!isIframeLoaded) {
          setLoadingTimeout(true);
          onLoad?.();
        }
      }, fallbackTimeout);

      setIsIframeLoaded(false);
      setLoadingTimeout(false);

      return () => clearTimeout(timeoutId);
    } else {
      return () => {};
    }
  }, [botId, mode, isIframeLoaded, onLoad]);

  const handleIframeLoad = () => {
    setIsIframeLoaded(true);
    onLoad?.();
  };

  if (mode === "inline") {
    return (
      <div className="flex items-center justify-center">
        <div className="w-full max-w-[210px] relative">
          <div
            id={containerId}
            ref={containerRef}
            className="w-full overflow-hidden relative"
            style={{
              height: "60px",
              width: "210px",
            }}
          >
            {!isIframeLoaded && !loadingTimeout && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin">
                    <HiOutlineSparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-gray-600 text-xs text-center px-2">
                    {loadingText}
                  </p>
                </div>
              </div>
            )}

            <iframe
              src={`${
                process.env.REACT_APP_LOUISE_BOT_WIDGET_PATH
              }/bots/${botId}?pageUrl=${encodeURIComponent(
                window.location.href
              )}&open=${open}&hideMinimizeButton=${hideMinimizeButton}&hideCloseButton=${hideCloseButton}&mute=${mute}`}
              allow="microphone"
              title="louisebot.com"
              name="louisebot.com"
              allowFullScreen
              onLoad={handleIframeLoad}
              className="absolute inset-0 w-full h-full"
              style={{
                border: "0px",
                background: "transparent",
                pointerEvents: "auto",
                overflow: "hidden",
                zIndex: 10,
                transition: "all 0.3s ease",
                opacity: isIframeLoaded || loadingTimeout ? 1 : 0,
                transform:
                  isIframeLoaded || loadingTimeout ? "scale(1)" : "scale(0.95)",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default LouiseBotWidget;
